/**
 * Revenue Area Chart Component
 * Shows invoiced vs collected revenue over time
 */

import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Box, Typography, useTheme } from "@mui/material";
import type { RevenueDataPoint } from "@/types/report";
import { formatCompactCurrency } from "@/utils/formatters";

interface RevenueAreaChartProps {
  data: RevenueDataPoint[];
  height?: number;
  showLegend?: boolean;
  title?: string;
}

const formatCurrency = (value: number): string => formatCompactCurrency(value);

const formatPeriod = (period: string): string => {
  // Handle both daily (YYYY-MM-DD) and monthly (YYYY-MM) formats
  if (period.length === 10) {
    // Daily format: YYYY-MM-DD -> "Dec 25"
    // Parse manually to avoid timezone issues (new Date("2024-12-25") is UTC midnight)
    const [year, month, day] = period.split("-").map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } else {
    // Monthly format: YYYY-MM -> "Dec '24"
    const [year, month] = period.split("-").map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  }
};

interface TooltipPayloadItem {
  value: number;
  name: string;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          boxShadow: 2,
        }}
      >
        <Typography variant="subtitle2" gutterBottom sx={{
          fontWeight: 600
        }}>
          {label}
        </Typography>
        {payload.map((entry: TooltipPayloadItem, index: number) => (
          <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: entry.color,
              }}
            />
            <Typography variant="body2">
              {entry.name}: {formatCurrency(entry.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

export const RevenueAreaChart: React.FC<RevenueAreaChartProps> = ({
  data,
  height = 300,
  showLegend = true,
  title,
}) => {
  const theme = useTheme();

  const chartData = data.map((dp) => ({
    period: dp.period,
    name: formatPeriod(dp.period),
    invoiced: dp.total_invoiced,
    collected: dp.total_collected,
    outstanding: dp.outstanding,
  }));

  const latestPoint = chartData[chartData.length - 1];
  const summaryText = latestPoint
    ? `Most recent period: ${latestPoint.name}, Invoiced: ${formatCurrency(latestPoint.invoiced)}, Collected: ${formatCurrency(latestPoint.collected)}`
    : "No revenue data available";

  return (
    <Box role="figure" aria-label={title || "Revenue Chart"}>
      {title && (
        <Typography variant="h6" gutterBottom sx={{
          fontWeight: 600
        }}>
          {title}
        </Typography>
      )}
      <Box className="sr-only" aria-live="polite">
        {summaryText}
      </Box>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="colorInvoiced" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
              <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorCollected" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.3} />
              <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
            interval="preserveStartEnd"
            minTickGap={50}
          />
          <YAxis
            tickFormatter={formatCurrency}
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend />}
          <Area
            type="monotone"
            dataKey="invoiced"
            name="Invoiced"
            stroke={theme.palette.primary.main}
            fillOpacity={1}
            fill="url(#colorInvoiced)"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="collected"
            name="Collected"
            stroke={theme.palette.success.main}
            fillOpacity={1}
            fill="url(#colorCollected)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RevenueAreaChart;
